import { useEffect, useState, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { User, Clock, Calendar, TrendingUp, TrendingDown, Briefcase, MapPin, Navigation } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';

function Profile() {
  const { user, API } = useContext(AppContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`);
      setProfile(response.data);
      setLocationEnabled(response.data.location_enabled || false);
      setCity(response.data.city || '');
      setCountry(response.data.country || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const requestGPSLocation = () => {
    setGettingLocation(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await axios.put(`${API}/user/location`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            location_enabled: true
          });
          toast.success('GPS location updated successfully');
          setLocationEnabled(true);
          fetchProfile();
        } catch (error) {
          console.error('Error updating GPS location:', error);
          toast.error('Failed to update GPS location');
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Failed to get your location. Please enable location permissions.');
        setGettingLocation(false);
      }
    );
  };

  const saveManualLocation = async () => {
    if (!city.trim() || !country.trim()) {
      toast.error('Please enter both city and country');
      return;
    }

    setSavingLocation(true);
    try {
      await axios.put(`${API}/user/location`, {
        city: city.trim(),
        country: country.trim(),
        location_enabled: true
      });
      toast.success('Location updated successfully');
      setLocationEnabled(true);
      fetchProfile();
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Failed to update location');
    } finally {
      setSavingLocation(false);
    }
  };

  const toggleLocation = async (enabled) => {
    try {
      await axios.put(`${API}/user/location`, {
        location_enabled: enabled
      });
      setLocationEnabled(enabled);
      toast.success(enabled ? 'Location features enabled' : 'Location features disabled');
      fetchProfile();
    } catch (error) {
      console.error('Error toggling location:', error);
      toast.error('Failed to update location settings');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading profile...</div>;
  }

  return (
    <div className="space-y-6" data-testid="profile">
      <div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Profile</h1>
        <p className="text-gray-600">Your account information and statistics</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info Card */}
        <Card className="bg-white shadow-lg lg:col-span-1" data-testid="profile-info-card">
          <CardHeader>
            <CardTitle>Account Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <User className="w-12 h-12 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800" data-testid="profile-username">{profile?.username}</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-xs text-gray-500">Available Tokens</p>
                  <p className="font-semibold text-purple-600" data-testid="profile-balance">{profile?.time_tokens?.toFixed(1)}</p>
                </div>
              </div>
              {profile?.escrow_tokens > 0 && (
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-xs text-gray-500">In Escrow</p>
                    <p className="font-semibold text-orange-600" data-testid="profile-escrow">{profile?.escrow_tokens?.toFixed(1)}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Member Since</p>
                  <p className="font-semibold text-gray-800">{new Date(profile?.member_since).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white shadow-lg" data-testid="stats-card">
            <CardHeader>
              <CardTitle>Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-gray-600">Active Services</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-600" data-testid="stats-services">{profile?.services_count}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-5 h-5 text-orange-600" />
                    <span className="text-sm text-gray-600">Services Requested</span>
                  </div>
                  <p className="text-3xl font-bold text-orange-600" data-testid="stats-sent">{profile?.transactions_sent}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-600">Services Provided</span>
                  </div>
                  <p className="text-3xl font-bold text-green-600" data-testid="stats-received">{profile?.transactions_received}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Location Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-800">Enable Location Features</p>
                  <p className="text-sm text-gray-600">Show distance to nearby listings</p>
                </div>
                <Switch
                  checked={locationEnabled}
                  onCheckedChange={toggleLocation}
                  data-testid="location-toggle"
                />
              </div>

              {locationEnabled && (
                <div className="space-y-4 pt-2">
                  {/* GPS Location */}
                  <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Navigation className="w-5 h-5 text-blue-600" />
                      <p className="font-medium text-gray-800">Use GPS Location</p>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Get your exact location for accurate distance calculations
                    </p>
                    <Button 
                      onClick={requestGPSLocation} 
                      disabled={gettingLocation}
                      className="w-full"
                      data-testid="gps-button"
                    >
                      {gettingLocation ? 'Getting Location...' : 'Use GPS'}
                    </Button>
                    {profile?.latitude && profile?.longitude && (
                      <p className="text-xs text-green-600 mt-2">
                        ✓ GPS location set
                      </p>
                    )}
                  </div>

                  {/* Manual Location */}
                  <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-purple-600" />
                      <p className="font-medium text-gray-800">Manual Location</p>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Enter your city and country manually
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          placeholder="e.g., London"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          data-testid="city-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="country">Country</Label>
                        <Input
                          id="country"
                          placeholder="e.g., United Kingdom"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          data-testid="country-input"
                        />
                      </div>
                      <Button 
                        onClick={saveManualLocation}
                        disabled={savingLocation}
                        variant="outline"
                        className="w-full"
                        data-testid="save-location-button"
                      >
                        {savingLocation ? 'Saving...' : 'Save Location'}
                      </Button>
                      {profile?.city && profile?.country && (
                        <p className="text-xs text-green-600">
                          ✓ Location: {profile.city}, {profile.country}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Privacy:</strong> Your exact location is never shared. Only approximate distance is shown to others.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Profile;